import { ApiError } from '@/api/ApiError'
import { BreadCrumbs } from '@/components/BreadCrumbs/BreadCrumbs'
import { CopyableValue } from '@/components/CopyableValue/CopyableValue'
import { SectionCard } from '@/components/SectionCard/SectionCard'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { hivesOverviewRoute } from '@/features/HivesOverview/pages/HivesOverviewHivesTable'
import { useOrgsBySalesforceAccount } from '@/features/SalesforceAccounts/api/salesforceAccountHooks'
import { AccountOrgsTable } from '@/features/SalesforceAccounts/components/AccountOrgsTable'
import { useCanManageSalesforceLink } from '@/features/SalesforceAccounts/hooks/useCanManageSalesforceLink'
import { isValidSalesforceAccountId } from '@/features/SalesforceAccounts/lib/salesforceOutcome'
import { ResolveIngestLink } from '@/features/SalesforceIngest/components/ResolveIngestLink'
import { salesforceAccountsRootRoute } from '@/router/rootRoutes'
import {
  Alert,
  Box,
  Button,
  Heading,
  Separator,
  Skeleton,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

export const salesforceAccountDetailRoute = createRoute({
  getParentRoute: () => salesforceAccountsRootRoute,
  path: '$salesforceAccountId',
  component: SalesforceAccountDetailPage,
})

/**
 * The HTTP status of a failed reverse lookup. The generated error type is a
 * lie at runtime — the post-processed fetcher throws `ApiError` or
 * `NetworkError` and nothing else — so the status is read off `ApiError`
 * directly. `0` for a failure that never got a response, which lands in the
 * generic "read failed" branch rather than claiming the service is disabled.
 */
function readFailureStatus(error: unknown): number {
  return error instanceof ApiError ? error.status : 0
}

/**
 * Every organisation linked to one Salesforce account — the reverse of the
 * org page's link card, and the surface that makes a duplicate organisation
 * visible.
 *
 * One card, dividers inside it, identity before the list: the account id is
 * what the rest of the page is about, so the organisations read as belonging
 * to it. Two sibling cards would present them as two unrelated things.
 *
 * The three answers this page can give are deliberately not interchangeable:
 *
 *   success + `[]`   nothing is linked — a neutral sentence, no alert
 *   `503`            the account service is off here, so we could not ask
 *   `502` or other   the read failed, so we could not ask
 *
 * "We could not ask" is never rendered as "there is none": reading one as the
 * other is what creates a second organisation on an account that already has
 * one. Hence the alert role on the failure states and its absence on the
 * empty one.
 */
export function SalesforceAccountDetailPage() {
  const { t } = useTranslation('features', { useSuspense: false })
  const params = salesforceAccountDetailRoute.useParams()
  const { canRead, isResolved } = useCanManageSalesforceLink()
  const navigate = useNavigate()

  const accountId = params.salesforceAccountId
  // The id comes from the URL, so it is whatever someone pasted. Checking here
  // means a malformed id never spends a request to be told what the pattern
  // already says.
  const isValidId = isValidSalesforceAccountId(accountId)

  const query = useOrgsBySalesforceAccount(accountId, {
    enabled: canRead && isValidId,
    retry: false,
  })

  // `isResolved` first, always. The roles query is still pending on the first
  // render of a hard page load, which makes `canRead` read false — redirecting
  // on that bounces every deep link, while in-app navigation works because
  // the roles are already cached.
  useEffect(() => {
    if (isResolved && !canRead) {
      void navigate({ to: hivesOverviewRoute.to })
    }
  }, [canRead, isResolved, navigate])

  const breadCrumbs = [
    {
      name: String(t('salesforceAccounts.detail.backToIndex')),
      path: salesforceAccountsRootRoute.to ?? '/salesforce-accounts',
    },
    { name: accountId, path: '' },
  ]

  // Nothing while the redirect runs: a skeleton would imply the account is
  // being loaded for a viewer who is not allowed to see it.
  if (isResolved && !canRead) {
    return null
  }

  const failureStatus = query.isError ? readFailureStatus(query.error) : null
  const isServiceDisabled = failureStatus === 503
  const orgs = query.data?.orgs ?? []

  return (
    <PageLayout
      // A node, not a string: PageLayout runs string titles through `t()`,
      // and the id in this one is a URL param with no business being used as
      // a translation key.
      title={
        <Box>
          {String(t('salesforceAccounts.detail.pageTitle', { accountId }))}
        </Box>
      }
      beforeTitle={<BreadCrumbs paths={breadCrumbs} />}
      action={<ResolveIngestLink />}
      maxWidth="8xl"
    >
      <SectionCard>
        <VStack align="stretch" gap={5}>
          {/* Identity first — the account this page is about. */}
          <VStack align="start" gap={1}>
            <Text fontSize="xs" color="text.text-subtle">
              {String(t('salesforceAccounts.detail.accountIdLabel'))}
            </Text>
            <CopyableValue value={accountId} fontFamily="mono" />
          </VStack>

          <Separator />

          <VStack align="stretch" gap={3}>
            <Heading as="h2" fontSize="md" m={0}>
              {String(t('salesforceAccounts.detail.orgs.title'))}
            </Heading>

            {!isValidId ? (
              <Alert.Root status="warning" role="alert">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>
                    {String(t('salesforceAccounts.detail.notFound.title'))}
                  </Alert.Title>
                  <Alert.Description>
                    {String(
                      t('salesforceAccounts.detail.notFound.description'),
                    )}
                  </Alert.Description>
                </Alert.Content>
              </Alert.Root>
            ) : null}

            {isValidId && (!isResolved || query.isLoading) ? (
              <Skeleton h={40} w="100%" />
            ) : null}

            {/* "We could not ask." Chakra's Alert.Root is a plain div, so the
                role is set explicitly: it is what keeps this state from being
                heard as the empty state below. */}
            {isValidId && !query.isLoading && query.isError ? (
              <Alert.Root status="warning" role="alert">
                <Alert.Indicator />
                <Alert.Content>
                  <Alert.Title>
                    {String(t('salesforceAccounts.detail.error.title'))}
                  </Alert.Title>
                  <Alert.Description>
                    {String(
                      t(
                        isServiceDisabled
                          ? 'salesforceAccounts.detail.error.serviceDisabled'
                          : 'salesforceAccounts.detail.error.description',
                      ),
                    )}
                  </Alert.Description>
                  <Box mt={2}>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={() => {
                        void query.refetch()
                      }}
                    >
                      {String(t('salesforceAccounts.detail.error.retry'))}
                    </Button>
                  </Box>
                </Alert.Content>
              </Alert.Root>
            ) : null}

            {/* Nothing is linked. A plain sentence, no alert role, no warning
                colour: this is a normal, complete answer. */}
            {isValidId &&
            !query.isLoading &&
            !query.isError &&
            orgs.length === 0 ? (
              <Text fontSize="sm" color="text.text-subtle">
                {String(t('salesforceAccounts.detail.orgs.empty'))}
              </Text>
            ) : null}

            {isValidId &&
            !query.isLoading &&
            !query.isError &&
            orgs.length > 0 ? (
              <AccountOrgsTable orgs={orgs} />
            ) : null}
          </VStack>
        </VStack>
      </SectionCard>
    </PageLayout>
  )
}
